import Model, { attr, belongsTo } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import type DigitalAssetModel from './digital-asset.ts';
import type { DocumentType } from '../types/tuition-assistance.ts';

interface IdentityCheck {
  detected_name: string | null;
  confidence: number;
}

const asRecord = (value: unknown) =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

export default class AssetSummaryModel extends Model {
  // Basic attributes
  @attr declare assetType: string;
  @attr declare createdBy: Record<string, unknown>;
  @attr declare etag: string;
  @attr declare quickSummary: Record<string, unknown>;
  @attr declare summary: Record<string, unknown>;
  @attr declare updatedBy: Record<string, unknown>;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  // Relationships
  @belongsTo('digital-asset', { async: false, inverse: 'assetSummary', polymorphic: true })
  declare digitalAsset: DigitalAssetModel;

  // Computed properties
  get hasSummary(): boolean {
    return Boolean(this.summary && Object.keys(this.summary).length > 0);
  }

  get hasQuickSummary(): boolean {
    return Boolean(this.quickSummary && Object.keys(this.quickSummary).length > 0);
  }

  get aiResult() {
    const summary = asRecord(this.summary);
    const result = asRecord(summary?.['result']);
    return result ?? summary;
  }

  /**
   * Categories the AI found in this document. Always at least one element when
   * the AI has run; empty array if the summary has not yet populated.
   */
  get detectedDocumentTypes(): DocumentType[] {
    const v =
      this.aiResult?.['detected_document_types'] ?? this.aiResult?.['detectedDocumentTypes'];
    return Array.isArray(v) ? (v as DocumentType[]) : [];
  }

  get identityCheck(): IdentityCheck {
    const v = asRecord(this.aiResult?.['identity_check'] ?? this.aiResult?.['identityCheck']);
    const detectedName = v?.['detected_name'] ?? v?.['detectedName'];
    const confidence = v?.['confidence'];
    return {
      detected_name: typeof detectedName === 'string' ? detectedName : null,
      confidence: typeof confidence === 'number' ? confidence : 0,
    };
  }

  get confidenceScore(): number {
    const v = this.aiResult?.['confidence_score'] ?? this.aiResult?.['confidenceScore'];
    return typeof v === 'number' ? v : 0;
  }

  /**
   * Does this summary's classification satisfy the given required type? `grade`
   * is treated as a subset of `transcript`.
   */
  satisfiesType(required: DocumentType): boolean {
    const detected = this.detectedDocumentTypes;
    if (detected.includes(required)) return true;
    if (required === 'grade' && detected.includes('transcript')) return true;
    return false;
  }

  [Type] = 'asset-summary' as const;
}
